import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  addDays,
  differenceInHours,
  endOfDay,
  format,
  startOfDay,
  subDays,
  subHours,
  toDate,
} from "date-fns";
import { t } from "i18next";
import { includes, isNil, min } from "ramda";
import { useCallback, useEffect, useState } from "react";
import { Icon } from "../../../common/Icon";
import { NumberInput } from "../../../common/NumberInput";

type HourFilter = 3 | 6 | 12 | 24 | 48 | 72 | "all";

type AgeFilter = [number, number];

export type TodayFilter =
  | "today"
  | "yesterday"
  | "before-yesterday"
  | "three-days-ago";

//"timeAndAge" affiche les boutons 24,48,72... ainsi qu'un filtre age min, age max. "age" n'affiche que le filtre ageMin ageMax, "time" n'affiche que le filtre 24,48,72h, "day" affiche un simple NumberInput avec un jour défini, "ageTwoDays" numberInput avec l'age et affiche le jour d'avant et le jour choisi.
type TimeMode = "timeAndAge" | "time" | "age" | "day" | "ageTwoDays" | "today";

// Types related to statistic
type DateYYYMMDD = string;

type APIStatisticFilter = {
  batchDays?: number;
  start?: DateYYYMMDD;
  end?: DateYYYMMDD;
  order?: "ASC" | "DESC";
  name: string; // StatisticName;
  frequency?: "hourly" | "daily" | "quarter";
  limit?: "-1" | number;
};

export type ApiDateFilter = {
  start: string;
  end: string;
  frequency: APIStatisticFilter["frequency"];
};

export type ChartTimeFilterState = {
  timeMode: TimeMode;
  hourFilter: HourFilter;
  ageFilter: AgeFilter;
  today: number; //Index of TODAY_FILTERS
};

const HOUR_FILTERS: HourFilter[] = [3, 6, 12, 24, 48, 72, "all"];

const TODAY_FILTERS: TodayFilter[] = [
  "today",
  "yesterday",
  "before-yesterday",
  "three-days-ago",
];

/*const usePreheatingOffsetDays = () => {
  const { preheatingCurve_offsetDay } = useFarmingConfigAdapter();
  return decodeArray(preheatingCurve_offsetDay)[0];
};*/

export const convertChartFilterToAPIFilter = (
  state: ChartTimeFilterState,
  refDate: Date | null,
  animalAge: number,
  preHeatingOffsetDays: number
) => {
  if (isNil(refDate)) {
    return undefined;
  }
  let dates = [];
  if (state.timeMode === "time") {
    if (state.hourFilter === "all") {
      dates = [
        format(
          startOfDay(subDays(refDate, animalAge - preHeatingOffsetDays)),
          "yyyy-MM-dd HH:mm:ss"
        ),
        format(endOfDay(refDate), "yyyy-MM-dd HH:mm:ss"),
      ];
    } else {
      dates = [
        format(subHours(refDate, state.hourFilter), "yyyy-MM-dd HH:mm:ss"),
        format(refDate, "yyyy-MM-dd HH:mm:ss"),
      ];
    }
  } else {
    const dateOfDay0 = subDays(refDate, animalAge);

    dates = [
      format(
        startOfDay(addDays(dateOfDay0, state.ageFilter[0])),
        "yyyy-MM-dd HH:mm:ss"
      ),
      format(
        endOfDay(addDays(dateOfDay0, state.ageFilter[1])),
        "yyyy-MM-dd HH:mm:ss"
      ),
    ];
  }
  return datesToApiDateFilter(dates);
};

export const datesToApiDateFilter = (
  dates: string[]
): ApiDateFilter | undefined =>
  dates[0] && dates[1]
    ? {
        start: dates[0],
        end: dates[1],
        frequency:
          isNil(dates[0]) || isNil(dates[1])
            ? undefined
            : differenceInHours(
                  toDate(format(dates[1], "yyyy-MM-dd HH:mm:ss")),
                  toDate(format(dates[0], "yyyy-MM-dd HH:mm:ss"))
                ) <= 24
              ? "quarter"
              : "hourly",
      }
    : undefined;

export const ChartTimeFilter = ({
  initialFilter,
  onChange,
  mode = "timeAndAge",
}: {
  initialFilter?: ChartTimeFilterState;
  onChange: (
    dateFilter: ChartTimeFilterState,
    apiDateFilter: ApiDateFilter | undefined
  ) => void;
  mode?: TimeMode;
}) => {
  const animalAge = 40; // const {animalAge} = useFarmingOutputAdapter();
  const preheatingOffset = 3; //usePreheatingOffsetDays();
  const refDate = toDate(Date.now()); // useSystemDateAdapter();

  const [filterState, setFilterState] = useState<ChartTimeFilterState>(
    initialFilter ?? {
      timeMode: includes("time", mode) ? "time" : "age",
      hourFilter: 24,
      ageFilter: [animalAge - 1, animalAge],
      today: 0,
    }
  );

  const change = useCallback(
    (newFilterState: ChartTimeFilterState) =>
      onChange(
        newFilterState,
        convertChartFilterToAPIFilter(
          newFilterState,
          refDate,
          animalAge,
          preheatingOffset
        )
      ),
    [refDate, animalAge, preheatingOffset, onChange]
  );

  useEffect(() => {
    const newFilterState: ChartTimeFilterState = {
      ...filterState,
      ageFilter: [
        min(animalAge, filterState.ageFilter[0]),
        min(animalAge, filterState.ageFilter[1]),
      ],
    };
    change(newFilterState);
    //eslint-disable-next-line
  }, [filterState]);

  let filters =
    animalAge > 0 ? HOUR_FILTERS : HOUR_FILTERS.filter((t) => t !== "all");

  const getCircleFilterClass = (isActive: boolean) =>
    `rounded-full flex items-center justify-center text-lg h-11 w-11 m-1.5 cursor-pointer border-2 transition-all ${
      isActive
        ? "text-grey-dark border-grey-medium bg-1dp shadow-sm"
        : "text-grey-medium border-grey-light hover:border-grey-medium"
    }`;

  return (
    <div
      className="flex items-center flex-wrap gap-y-2"
      style={{ marginLeft: "8.5%" }}
    >
      {includes("time", mode) &&
        filters.map((f: HourFilter, i: number) => {
          return (
            <button
              type="button"
              key={i}
              onClick={() => {
                setFilterState((prev) => ({
                  ...prev,
                  timeMode: "time",
                  hourFilter: f,
                }));
              }}
              className={getCircleFilterClass(
                f === filterState.hourFilter && filterState.timeMode === "time"
              )}
              data-testid={f}
            >
              {f !== "all" ? `${f}h` : <Icon icon="chicken" size={1.5} />}
            </button>
          );
        })}
      {animalAge > 0 && (mode === "timeAndAge" || mode === "age") && (
        <div className="flex items-center ml-2">
          {mode === "timeAndAge" && (
            <button
              type="button"
              onClick={() => {
                setFilterState((prev) => ({
                  ...prev,
                  timeMode: "age",
                }));
              }}
              className={getCircleFilterClass(filterState.timeMode === "age")}
              data-testid="ageModeButton"
            >
              <FontAwesomeIcon icon="calendar" />
            </button>
          )}
          <div
            className={`flex items-center text-med gap-1 ${
              filterState.timeMode !== "age" ? "opacity-30" : ""
            }`}
          >
            {mode === "age" && (
              <FontAwesomeIcon className="text-lg" icon="calendar" />
            )}
            <div className="mx-1">
              <NumberInput
                regulationValue={filterState.ageFilter[0]}
                label={t("animal-min-age")}
                onChange={(v) => {
                  setFilterState((prev) => ({
                    ...prev,
                    timeMode: "age",
                    ageFilter: [v, prev.ageFilter[1]],
                  }));
                }}
                classWidth="w-52"
                fixedDigits={0}
              />
            </div>
            <div className="h-10 w-10 rounded-full flex items-center justify-center text-grey-medium">
              <FontAwesomeIcon icon="arrow-right" className="text-sm" />
            </div>
            <div className="mx-1">
              <NumberInput
                regulationValue={filterState.ageFilter[1]}
                label={t("animal-max-age")}
                onChange={(v) => {
                  setFilterState((prev) => ({
                    ...prev,
                    timeMode: "age",
                    ageFilter: [prev.ageFilter[0], v],
                  }));
                }}
                classWidth="w-52"
                fixedDigits={0}
              />
            </div>
          </div>
        </div>
      )}
      {animalAge > 0 && mode === "day" && (
        <>
          <FontAwesomeIcon className="text-lg" icon="calendar" />
          <div className="m-2">
            <NumberInput
              regulationValue={filterState.ageFilter[1]}
              label={t("animal-age")}
              onChange={(v) => {
                setFilterState((prev) => ({
                  ...prev,
                  timeMode: "day",
                  ageFilter: [v, v],
                }));
              }}
              classWidth="w-52"
              fixedDigits={0}
            />
          </div>
        </>
      )}

      {animalAge > 0 && mode === "ageTwoDays" && (
        <>
          <FontAwesomeIcon className="text-lg" icon="calendar" />
          <div className="m-2">
            <NumberInput
              regulationValue={filterState.ageFilter[1]}
              label={t("animal-age")}
              onChange={(v) => {
                if (v > 1) {
                  setFilterState((prev) => ({
                    ...prev,
                    timeMode: "age",
                    ageFilter: [v - 1, v], //on affiche le jour choisi et le jour d'avant
                  }));
                } else {
                  setFilterState((prev) => ({
                    ...prev,
                    timeMode: "age",
                    ageFilter: [v, v], //si v=0 on affiche juste le jour
                  }));
                }
              }}
              classWidth="w-52"
              fixedDigits={0}
            />
          </div>
        </>
      )}
      {mode === "today" &&
        TODAY_FILTERS.map((f: TodayFilter, index) => {
          let className = `rounded-full flex items-center text-lg h-12 w-64 p-2 bg-3dp m-2 ${
            index === filterState.today
              ? "text-grey-dark border-2 border-grey-medium"
              : "text-grey-medium border-2 border-grey-light"
          }`;
          return (
            <div
              key={index}
              onClick={() => {
                setFilterState((prev) => ({
                  ...prev,
                  today: index,
                }));
              }}
              className={className}
              data-testid={"ageModeButton"}
            >
              {f}
              {
                //t(f)
              }
            </div>
          );
        })}
    </div>
  );
};
